/**
 * Policies View - Admin
 * Configure access policies with rule editor and compliance monitoring
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  Copy,
  Save,
  Play,
  Lock,
  Globe,
  Users,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface Policy {
  id: string;
  name: string;
  description: string;
  type: "rate_limit" | "content_filter" | "access_control" | "data_retention";
  status: "active" | "draft" | "disabled";
  priority: "low" | "medium" | "high" | "critical";
  rules: Rule[];
  createdAt: string;
  updatedAt: string;
  compliance: number; // 0-100
  violations: number;
}

interface Rule {
  id: string;
  field: string;
  operator: "equals" | "contains" | "matches" | "greater_than" | "less_than" | "in";
  value: string | number | string[];
  action: "allow" | "block" | "flag" | "limit";
}

const MOCK_POLICIES: Policy[] = [
  {
    id: "1",
    name: "Rate Limiting - Free Tier",
    description: "Limits API calls for free tier users",
    type: "rate_limit",
    status: "active",
    priority: "high",
    rules: [
      {
        id: "r1",
        field: "user.tier",
        operator: "equals",
        value: "free",
        action: "limit",
      },
      {
        id: "r2",
        field: "requests.per_day",
        operator: "greater_than",
        value: 1000,
        action: "block",
      },
    ],
    createdAt: "2025-01-15T10:00:00Z",
    updatedAt: "2025-12-01T10:00:00Z",
    compliance: 100,
    violations: 0,
  },
  {
    id: "2",
    name: "Content Moderation",
    description: "Filters harmful content in AGI responses",
    type: "content_filter",
    status: "active",
    priority: "critical",
    rules: [
      {
        id: "r3",
        field: "response.content",
        operator: "contains",
        value: ["violence", "hate", "harassment"],
        action: "flag",
      },
    ],
    createdAt: "2025-02-01T09:00:00Z",
    updatedAt: "2025-12-15T14:30:00Z",
    compliance: 98,
    violations: 12,
  },
  {
    id: "3",
    name: "Data Retention Policy",
    description: "Automatically deletes old logs and data",
    type: "data_retention",
    status: "active",
    priority: "medium",
    rules: [
      {
        id: "r4",
        field: "data.age",
        operator: "greater_than",
        value: 90,
        action: "block",
      },
    ],
    createdAt: "2025-03-10T11:00:00Z",
    updatedAt: "2025-11-20T16:00:00Z",
    compliance: 100,
    violations: 0,
  },
  {
    id: "4",
    name: "Admin Access Control",
    description: "Restricts admin access to authorized users",
    type: "access_control",
    status: "active",
    priority: "critical",
    rules: [
      {
        id: "r5",
        field: "user.role",
        operator: "in",
        value: ["master", "creator"],
        action: "allow",
      },
    ],
    createdAt: "2025-01-01T00:00:00Z",
    updatedAt: "2025-12-28T09:00:00Z",
    compliance: 100,
    violations: 0,
  },
];

const POLICY_TYPES = [
  { id: "rate_limit", label: "Rate Limiting", icon: Clock, color: "text-blue-500" },
  { id: "content_filter", label: "Content Filter", icon: Shield, color: "text-red-500" },
  { id: "access_control", label: "Access Control", icon: Lock, color: "text-green-500" },
  { id: "data_retention", label: "Data Retention", icon: Globe, color: "text-purple-500" },
];

const PRIORITY_COLORS = {
  low: "bg-gray-500/10 text-gray-500",
  medium: "bg-blue-500/10 text-blue-500",
  high: "bg-orange-500/10 text-orange-500",
  critical: "bg-red-500/10 text-red-500",
};

export function PoliciesView() {
  const { addLogEntry } = useDashboardStore();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setPolicies(MOCK_POLICIES);
    } catch (error) {
      console.error("Failed to load policies:", error);
      addLogEntry({
        category: "error",
        action: "Load Policies",
        details: String(error),
        severity: "error",
        source: "policies",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePolicyStatus = async (id: string) => {
    try {
      setPolicies(policies.map(p => {
        if (p.id === id) {
          const newStatus = p.status === "active" ? "disabled" : "active";
          addLogEntry({
            category: "security",
            action: "Policy Status Changed",
            details: `Policy ${p.name} set to ${newStatus}`,
            severity: "info",
            source: "policies",
          });
          return { ...p, status: newStatus as any };
        }
        return p;
      }));
    } catch (error) {
      console.error("Failed to toggle policy:", error);
    }
  };

  const deletePolicy = async (id: string) => {
    try {
      setPolicies(policies.filter(p => p.id !== id));
      addLogEntry({
        category: "security",
        action: "Policy Deleted",
        details: `Policy ${id} deleted`,
        severity: "warning",
        source: "policies",
      });
    } catch (error) {
      console.error("Failed to delete policy:", error);
    }
  };

  const duplicatePolicy = async (policy: Policy) => {
    try {
      const newPolicy: Policy = {
        ...policy,
        id: Date.now().toString(),
        name: `${policy.name} (Copy)`,
        status: "draft",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setPolicies([newPolicy, ...policies]);
      addLogEntry({
        category: "security",
        action: "Policy Duplicated",
        details: `Policy ${policy.name} duplicated`,
        severity: "info",
        source: "policies",
      });
    } catch (error) {
      console.error("Failed to duplicate policy:", error);
    }
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = 
      policy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      policy.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || policy.type === typeFilter;
    const matchesStatus = statusFilter === "all" || policy.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const activePolicies = policies.filter(p => p.status === "active").length;
  const avgCompliance = policies.length > 0 
    ? Math.round(policies.reduce((sum, p) => sum + p.compliance, 0) / policies.length) 
    : 0;
  const totalViolations = policies.reduce((sum, p) => sum + p.violations, 0);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Loading policies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Policies</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Configure access and compliance policies</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
        >
          <Plus className="h-4 w-4" />
          Create Policy
        </button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Shield className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{policies.length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Policies</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activePolicies}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Active</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
              <Users className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{avgCompliance}%</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Avg Compliance</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalViolations}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Violations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-between rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text"
            placeholder="Search policies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>
        
        <div className="flex gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          >
            <option value="all">All Types</option>
            {POLICY_TYPES.map(type => (
              <option key={type.id} value={type.id}>{type.label}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {/* Policies Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        <AnimatePresence>
          {filteredPolicies.map((policy, index) => {
            const policyType = POLICY_TYPES.find(t => t.id === policy.type);
            const TypeIcon = policyType?.icon || Shield;
            
            return (
              <motion.div
                key={policy.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  "rounded-xl border bg-[hsl(var(--card))] p-6",
                  policy.status === "active" ? "border-[hsl(var(--border))]" : "border-dashed border-[hsl(var(--muted-foreground))]/30"
                )}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", policy.status === "active" ? "bg-[hsl(var(--muted))]" : "bg-[hsl(var(--muted))]/50")}>
                      <TypeIcon className={cn("h-5 w-5", policyType?.color)} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{policy.name}</h3>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{policyType?.label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={cn("rounded px-2.5 py-1 text-xs font-medium", PRIORITY_COLORS[policy.priority])}>
                      {policy.priority}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{policy.description}</p>

                {/* Rules */}
                <div className="mb-4 space-y-2">
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Rules ({policy.rules.length})</p>
                  <div className="space-y-1.5">
                    {policy.rules.slice(0, 2).map((rule, i) => (
                      <div key={rule.id} className="flex items-center gap-2 rounded-md bg-[hsl(var(--muted))] px-3 py-1.5 text-xs">
                        <code className="font-mono">{rule.field}</code>
                        <span className="text-[hsl(var(--muted-foreground))]">{rule.operator}</span>
                        <code className="font-mono">{typeof rule.value === "string" && rule.value.includes(",") ? `[${rule.value}]` : rule.value}</code>
                        <span className={cn(
                          "ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium",
                          rule.action === "allow" ? "bg-green-500/10 text-green-500" :
                          rule.action === "block" ? "bg-red-500/10 text-red-500" :
                          rule.action === "flag" ? "bg-amber-500/10 text-amber-500" :
                          "bg-blue-500/10 text-blue-500"
                        )}>
                          {rule.action}
                        </span>
                      </div>
                    ))}
                    {policy.rules.length > 2 && (
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        +{policy.rules.length - 2} more rules
                      </p>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-[hsl(var(--border))] pt-4">
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className={cn("h-2 w-2 rounded-full", policy.status === "active" ? "bg-green-500" : "bg-gray-500")} />
                      {policy.status}
                    </div>
                    <div className="flex items-center gap-1">
                      <Shield className={cn("h-3 w-3", policy.compliance === 100 ? "text-green-500" : "text-amber-500")} />
                      {policy.compliance}% compliant
                    </div>
                    {policy.violations > 0 && (
                      <div className="flex items-center gap-1 text-red-500">
                        <AlertTriangle className="h-3 w-3" />
                        {policy.violations} violations
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => duplicatePolicy(policy)}
                      className="rounded p-1.5 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                    >
                      <Copy className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />
                    </button>
                    <button
                      onClick={() => togglePolicyStatus(policy.id)}
                      className={cn(
                        "rounded p-1.5 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer",
                        policy.status === "active" ? "text-green-500" : "text-gray-500"
                      )}
                    >
                      {policy.status === "active" ? <XCircle className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      onClick={() => deletePolicy(policy.id)}
                      className="rounded p-1.5 hover:bg-red-500/10 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {filteredPolicies.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
          <FileText className="h-12 w-12 text-[hsl(var(--muted-foreground))]" />
          <h3 className="mt-4 text-lg font-semibold">No policies found</h3>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Create a new policy to get started</p>
        </div>
      )}

      {/* Create Policy Modal - Placeholder */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Create Policy</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="rounded p-1 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                >
                  <XCircle className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                </button>
              </div>
              
              <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-8 text-center">
                <FileText className="mx-auto h-12 w-12 text-[hsl(var(--muted-foreground))]" />
                <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
                  Policy editor with rule builder coming soon
                </p>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-lg border border-[hsl(var(--border))] py-2.5 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button className="flex-1 rounded-lg bg-[hsl(var(--primary))] py-2.5 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer">
                  Create Policy
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
